import React, { useMemo } from 'react';
import { Trash2, Plus, Clock as ClockIcon } from 'lucide-react';
import Clock from '../../../../../../../components/forms/Clock';

const DayEventsEditor = ({ events = [], remarks = [], timings = [], dayIndex = 0, onChange }) => {
  // Normalize events: if events is empty but legacy remarks exist, synthesize events
  const safeEvents = useMemo(() => {
    if (Array.isArray(events) && events.length > 0) {
      return events.map((e, idx) => ({
        id: e.id || `evt_${dayIndex + 1}_${idx + 1}`,
        title: e.title !== undefined ? e.title : (e.activity || ''),
        time: e.time || (timings && timings[idx]) || null,
        time_end: e.time_end || null,
        time_context: e.time_context || null,
        tag: e.tag || 'other',
        description: e.description || null
      }));
    }
    if (Array.isArray(remarks) && remarks.length > 0) {
      return remarks.map((r, rIdx) => ({
        id: `evt_${dayIndex + 1}_${rIdx + 1}`,
        title: r || '',
        time: (timings && timings[rIdx]) || null,
        time_end: null,
        time_context: null,
        tag: 'other',
        description: null
      }));
    }
    return [
      {
        id: `evt_${dayIndex + 1}_1_${Math.random().toString(36).substr(2, 5)}`,
        title: '',
        time: null,
        time_end: null,
        time_context: null,
        tag: 'other',
        description: null
      }
    ];
  }, [events, remarks, timings, dayIndex]);

  const notifyChange = (updated) => {
    onChange(updated);
  };

  const handleUpdate = (eventIdx, field, val) => {
    const updated = safeEvents.map((evt, idx) => {
      if (idx !== eventIdx) return evt;
      return { ...evt, [field]: val };
    });
    notifyChange(updated);
  };

  const handleAddEvent = () => {
    const newEvt = {
      id: `evt_${dayIndex + 1}_${safeEvents.length + 1}_${Math.random().toString(36).substr(2, 5)}`,
      title: '',
      time: null,
      time_end: null,
      time_context: null,
      tag: 'other',
      description: null
    };
    notifyChange([...safeEvents, newEvt]);
  };

  const handleRemoveEvent = (eventIdx) => {
    const updated = safeEvents.filter((_, idx) => idx !== eventIdx);
    notifyChange(updated);
  };

  return (
    <div className="space-y-3">
      {/* Mobile Event Cards (<640px) */}
      <div className="sm:hidden space-y-2.5">
        {safeEvents.map((evt, evtIdx) => (
          <div key={evt.id || evtIdx} className="bg-slate-50/80 border border-slate-200/90 rounded-xl p-2.5 space-y-2 shadow-2xs">
            <div className="flex items-center justify-between gap-2">
              <div className="w-36 xs:w-40">
                <Clock
                  value={evt.time || ''}
                  onChange={(val) => handleUpdate(evtIdx, 'time', val || null)}
                  placeholder="Select Time"
                  inputClassName="h-[36px] px-2.5 rounded-lg text-xs font-normal text-slate-700 outline-none border-slate-200 bg-white"
                />
              </div>
              <button
                type="button"
                onClick={() => handleRemoveEvent(evtIdx)}
                className="text-slate-400 hover:text-rose-500 p-1.5 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer"
                title="Remove Event"
              >
                <Trash2 size={16} />
              </button>
            </div>
            <div>
              <input
                type="text"
                value={evt.title || ''}
                placeholder={`Event #${evtIdx + 1} (e.g. Check-in to hotel & welcome drink)`}
                onChange={(e) => handleUpdate(evtIdx, 'title', e.target.value)}
                className="w-full bg-white border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary rounded-lg px-3 h-[36px] text-xs font-normal text-slate-700 outline-none transition-all placeholder:font-normal placeholder:text-slate-400"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table for Time and Events (>=640px) */}
      <div className="hidden sm:block overflow-x-auto bg-white border border-slate-200 rounded-2xl shadow-xs">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/60">
              <th className="py-3 px-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-36 sm:w-44">
                <span className="flex items-center gap-1.5">
                  <ClockIcon size={12} /> TIME
                </span>
              </th>
              <th className="py-3 px-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                EVENT
              </th>
              <th className="py-3 px-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-12 text-center">
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {safeEvents.map((evt, evtIdx) => (
              <tr key={evt.id || evtIdx} className="hover:bg-slate-50/40 transition-colors">
                {/* TIME Column */}
                <td className="py-2.5 px-4 align-middle w-36 sm:w-44">
                  <Clock
                    value={evt.time || ''}
                    onChange={(val) => handleUpdate(evtIdx, 'time', val || null)}
                    placeholder="Select Time"
                    inputClassName="h-[40px] px-3 rounded-xl text-xs font-normal text-slate-700 outline-none border-slate-200 bg-white"
                  />
                </td>

                {/* EVENT Column */}
                <td className="py-2.5 px-4 align-middle">
                  <input
                    type="text"
                    value={evt.title || ''}
                    placeholder={`Event #${evtIdx + 1} (e.g. Check-in to hotel & welcome drink)`}
                    onChange={(e) => handleUpdate(evtIdx, 'title', e.target.value)}
                    className="w-full bg-white border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary rounded-xl px-3.5 h-[40px] text-xs font-normal text-slate-700 outline-none transition-all placeholder:font-normal placeholder:text-slate-400"
                  />
                </td>

                {/* Remove Event Action */}
                <td className="py-2.5 px-3 align-middle text-center w-12">
                  <button
                    type="button"
                    onClick={() => handleRemoveEvent(evtIdx)}
                    className="text-slate-400 hover:text-rose-500 transition-colors p-1.5 rounded-lg hover:bg-rose-50 cursor-pointer"
                    title="Remove Event"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pt-1">
        <button
          onClick={handleAddEvent}
          type="button"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-teal-700 transition-colors cursor-pointer"
        >
          <Plus size={15} /> Add event
        </button>
      </div>
    </div>
  );
};

export default DayEventsEditor;
